(function () {
  const DEBUG_KEY = "venueFormDebug";
  const STORAGE_PREFIX = "venueFormAnalytics";

  const EVENTS = new Set([
    "venue_form_opened",
    "venue_form_started",
    "venue_form_step_viewed",
    "venue_form_step_completed",
    "venue_form_submit_clicked",
    "venue_form_submit_succeeded",
    "venue_form_submit_failed"
  ]);

  const EVENT_PROPERTIES = {
    venue_form_opened: ["venue_form_id", "form_mode", "total_steps"],
    venue_form_started: ["venue_form_id", "form_mode"],
    venue_form_step_viewed: ["venue_form_id", "step", "step_name"],
    venue_form_step_completed: ["venue_form_id", "step", "step_name"],
    venue_form_submit_clicked: ["venue_form_id", "form_mode"],
    venue_form_submit_succeeded: [
      "venue_form_id",
      "form_mode",
      "duration_active_seconds",
      "duration_fill_seconds",
      "duration_open_to_submit_seconds",
      "submit_processing_seconds",
      "error_count",
      "corrected_field_count",
      "completed_after_errors",
      "save_count"
    ],
    venue_form_submit_failed: ["venue_form_id", "form_mode", "error_type", "field"]
  };

  function now() {
    return Date.now();
  }

  function toSnakeCase(key = "") {
    return String(key)
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase();
  }

  function safeValue(value) {
    if (typeof value === "number" || typeof value === "boolean") return value;
    return String(value).slice(0, 160);
  }

  function shouldIncludeValue(value) {
    return value !== null && value !== undefined && value !== "";
  }

  function toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function toDurationSeconds(ms) {
    return Math.round(Math.max(0, toNumber(ms, 0)) / 100) / 10;
  }

  function getFailureField(details = {}, error = null, context = {}) {
    if (details.field) return details.field;
    if (context.field) return context.field;
    if (details.type === "file_upload_failed") return "file_upload";
    if (details.type === "api_error" || error instanceof TypeError) return "form";
    return "";
  }

  function getStorageKey(context = {}) {
    const id = context.venueFormId || context.venue_form_id || context.productHandle || context.product_handle || "unknown";
    return `${STORAGE_PREFIX}:${id}`;
  }

  function readState(context = {}) {
    try {
      return JSON.parse(window.localStorage.getItem(getStorageKey(context)) || "{}") || {};
    } catch (error) {
      return {};
    }
  }

  function writeState(context = {}, state = {}) {
    try {
      window.localStorage.setItem(getStorageKey(context), JSON.stringify(state));
    } catch (error) {}
    return state;
  }

  function normalizeContext(context = {}) {
    const payload = {};
    Object.entries(context || {}).forEach(([key, value]) => {
      if (typeof value === "function") return;
      if (!shouldIncludeValue(value)) return;
      payload[toSnakeCase(key)] = safeValue(value);
    });
    return payload;
  }

  function buildPayload(event, context = {}) {
    const normalized = normalizeContext(context);
    const payload = { event };
    const allowedProperties = EVENT_PROPERTIES[event] || [];

    allowedProperties.forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(normalized, field)) return;
      if (!shouldIncludeValue(normalized[field])) return;
      payload[field] = normalized[field];
    });

    return payload;
  }

  function sendDataLayerEvent(event, payload = {}) {
    if (!EVENTS.has(event)) return;
    window.dataLayer = window.dataLayer || [];
    const eventPayload = buildPayload(event, payload);
    window.dataLayer.push(eventPayload);
    if (observability.debug) console.info("[venue_form]", event, eventPayload);
  }

  const observability = {
    sessionContext: {},
    state: {},
    formVisibleAt: 0,
    firstInputAt: 0,
    submitStartedAt: 0,
    hasRecordedVenueOpen: false,
    hasRecordedStarted: false,
    firstInteractionBound: false,
    lastViewedStep: null,
    pendingStepViewedContext: null,
    completedStepEvents: new Set(),
    errorCount: 0,
    correctedFieldCount: 0,
    errorFields: new Set(),
    stepErrorFields: new Map(),
    debug: false,

    configure(context = {}) {
      this.debug = this.debug || window.localStorage?.getItem(DEBUG_KEY) === "true";
      this.sessionContext = {
        ...this.sessionContext,
        ...context
      };
      this.state = { ...readState(this.sessionContext), ...this.state };
    },

    getBaseContext(context = {}) {
      return {
        ...this.sessionContext,
        ...this.state,
        ...context
      };
    },

    sendEvent(eventName, payload = {}) {
      sendDataLayerEvent(eventName, this.getBaseContext(payload));
    },

    startSession(form, context = {}) {
      this.configure(context);
      this.formVisibleAt = this.formVisibleAt || now();
      this.bindFirstInteraction(form);
    },

    bindFirstInteraction(form) {
      if (!form || this.firstInteractionBound) return;
      this.firstInteractionBound = true;
      const setFirstInput = () => {
        this.firstInputAt = this.firstInputAt || now();
        this.recordStarted();
        form.removeEventListener("input", setFirstInput, true);
        form.removeEventListener("change", setFirstInput, true);
      };
      form.addEventListener("input", setFirstInput, true);
      form.addEventListener("change", setFirstInput, true);
    },

    recordFormOpen(context = {}) {
      if (this.hasRecordedVenueOpen) return;
      this.configure(context);
      this.formVisibleAt = this.formVisibleAt || now();
      this.hasRecordedVenueOpen = true;
      this.sendEvent("venue_form_opened", context);
      if (this.pendingStepViewedContext) {
        const stepContext = this.pendingStepViewedContext;
        this.pendingStepViewedContext = null;
        this.recordStepViewed(stepContext);
      }
    },

    recordStarted(context = {}) {
      if (this.hasRecordedStarted) return;
      this.hasRecordedStarted = true;
      this.firstInputAt = this.firstInputAt || now();
      this.sendEvent("venue_form_started", context);
    },

    recordStepViewed(context = {}) {
      const step = toNumber(context.step, 0);
      if (!step || this.lastViewedStep === step) return;
      if (!this.hasRecordedVenueOpen) {
        this.pendingStepViewedContext = context;
        return;
      }
      this.lastViewedStep = step;
      this.sendEvent("venue_form_step_viewed", context);
    },

    recordStepCompleted(context = {}) {
      const step = toNumber(context.step, 0);
      if (!step || this.completedStepEvents.has(step)) return;
      this.completedStepEvents.add(step);
      this.sendEvent("venue_form_step_completed", context);
    },

    recordValidation(errors = [], context = {}) {
      const currentErrors = Array.isArray(errors) ? errors.filter(Boolean) : [];
      if (!currentErrors.length) {
        this.resolveAllErrorFields();
        return;
      }
      this.trackValidationErrors(currentErrors, context);
    },

    recordNextClick(errors = [], context = {}) {
      const currentErrors = Array.isArray(errors) ? errors.filter(Boolean) : [];
      const step = toNumber(context.step, 0);
      if (!currentErrors.length) {
        this.resolveStepErrorFields(step);
        return;
      }
      this.trackValidationErrors(currentErrors, context);
    },

    recordSubmitStart(context = {}) {
      this.submitStartedAt = now();
      this.sendEvent("venue_form_submit_clicked", context);
    },

    recordSubmitSuccess(context = {}, options = {}) {
      const isDraft = Boolean(options.isDraftSave);
      const successAt = toNumber(options.backendSucceededAt, now());
      const saveCount = toNumber(this.state.save_count, 0) + (isDraft ? 1 : 0);
      this.state = writeState(this.sessionContext, {
        ...this.state,
        save_count: saveCount
      });

      this.sendEvent("venue_form_submit_succeeded", {
        ...context,
        durationActiveSeconds: this.formVisibleAt ? toDurationSeconds(successAt - this.formVisibleAt) : 0,
        durationFillSeconds: this.firstInputAt ? toDurationSeconds(successAt - this.firstInputAt) : 0,
        durationOpenToSubmitSeconds: this.submitStartedAt && this.formVisibleAt
          ? toDurationSeconds(this.submitStartedAt - this.formVisibleAt)
          : 0,
        submitProcessingSeconds: this.submitStartedAt ? toDurationSeconds(successAt - this.submitStartedAt) : 0,
        errorCount: this.errorCount,
        correctedFieldCount: this.correctedFieldCount,
        completedAfterErrors: this.errorCount > 0,
        saveCount
      });
    },

    recordSubmitFailure(error, context = {}) {
      const details = error?.details || {};
      this.sendEvent("venue_form_submit_failed", {
        ...context,
        errorType: details.type || error?.name || "unknown",
        field: getFailureField(details, error, context)
      });
    },

    trackValidationErrors(errors = [], context = {}) {
      const step = toNumber(context.step, 0);
      const fields = errors.map((error) => error.field).filter(Boolean);
      this.errorCount += errors.length;
      fields.forEach((field) => this.errorFields.add(field));

      if (step && fields.length) {
        this.stepErrorFields.set(step, new Set(fields));
      }
    },

    resolveStepErrorFields(step) {
      if (!step || !this.stepErrorFields.has(step)) return;
      const fields = this.stepErrorFields.get(step);
      fields.forEach((field) => this.errorFields.delete(field));
      this.correctedFieldCount += fields.size;
      this.stepErrorFields.delete(step);
    },

    resolveAllErrorFields() {
      if (!this.errorFields.size) return;
      this.correctedFieldCount += this.errorFields.size;
      this.errorFields.clear();
      this.stepErrorFields.clear();
    }
  };

  observability.configure();
  window.VenueFormObservability = observability;
})();
